/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import { Fragment } from 'react';
import { useStoreState } from 'easy-peasy';
import { LoadingSpinner } from '@checkout/react-components';

// Internal modules
import { logBlurOrFocusEvent } from './utils';
import { guestContactStyles } from './styles';
import { SubmitButton } from '../SubmitButton';
import { ContactInfo } from '../ContactInfo';
import { features, getContent, getGlobals, useMountLogging } from '@/lib';
import { SignupFields } from '../SignupFields';
import { OnboardOptions } from '../OnboardOptions';
import { PaymentInformation } from '../PaymentInformation';
import {
  AccountFieldsContainer,
  CobrandedDisclaimer,
  Section,
} from './StyledComponents';
import { blurHandler, changeHandler, submitHandler } from './handlers';
import {
  AccountSectionHeader,
  LazyAddress,
  LazyAddressNormalizedAddressInterstitial,
  LazyInstallmentInterstitial,
  LazyOneTimePasswordConfirmationInterstitial,
  LazyPasswordlessOnboarding,
  LazyPhoneConfirmationInterstitial,
  LazySepaInterstitial,
  LazyShipping,
  LazyThreeDsInterstitial,
  ReturnAfterSubmitMessage,
} from './Components';

const Form = props => {
  useMountLogging('Form');

  const handlers = {
    blurHandler,
    changeHandler,
    focusHandler: logBlurOrFocusEvent,
  };

  // State
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const isInstallmentInterstitialShown = useStoreState(
    ({ ui }) => ui.isInstallmentInterstitialShown,
  );
  const isThreeDS1Shown = useStoreState(({ ui }) => ui.isThreeDS1Shown);
  const isAddressNormalizeOpen = useStoreState(
    ({ ui }) => ui.isAddressNormalizeOpen,
  );
  const isSepaInterstitialOpen = useStoreState(
    ({ ui }) => ui.isSepaInterstitialOpen,
  );
  const isShippingShown = useStoreState(({ ui }) => ui.isShippingShown);
  const isPhoneConfirmationOpen = useStoreState(
    ({ ui }) => ui.isPhoneConfirmationOpen,
  );
  const isOneTimePasswordConfirmationOpen = useStoreState(
    ({ ui }) => ui.isOneTimePasswordConfirmationOpen,
  );
  const cardCobrand = useStoreState(({ form }) => form.cardNumber.cobrand);
  const merchantCobrands =
    getGlobals().initialGqlData.checkoutSession.merchant.cobrands || [];

  // Features
  const isGuestEligible = features.isActive('isGuestEligible');
  const isPomaEligible = features.isActive('isPOMAEligible');
  const withAddress = features.isNotActive('isAddressless');

  return (
    <div css={{ position: 'relative ' }}>
      <form noValidate onSubmit={submitHandler}>
        <PaymentInformation />

        {withAddress && <LazyAddress {...handlers} />}

        {isShippingShown && <LazyShipping />}

        {isGuestEligible || isPomaEligible ? (
          <Fragment>
            <Section css={guestContactStyles}>
              <ContactInfo {...handlers} />
            </Section>

            <Section>
              {isPomaEligible ? (
                <LazyPasswordlessOnboarding />
              ) : (
                <Fragment>
                  <AccountSectionHeader />
                  <OnboardOptions {...handlers} />
                </Fragment>
              )}
            </Section>
          </Fragment>
        ) : (
          <Section>
            <AccountSectionHeader />

            <AccountFieldsContainer>
              <ContactInfo {...handlers} />

              <SignupFields {...handlers} />
            </AccountFieldsContainer>
          </Section>
        )}

        {merchantCobrands.includes(cardCobrand) && (
          <CobrandedDisclaimer>
            {getContent('disclaimer.cobranded')}
          </CobrandedDisclaimer>
        )}

        {features.isActive('hasReturnAfterSubmitMessage') && (
          <ReturnAfterSubmitMessage />
        )}

        <SubmitButton />
      </form>

      {isThreeDS1Shown && <LazyThreeDsInterstitial />}
      {isAddressNormalizeOpen && <LazyAddressNormalizedAddressInterstitial />}
      {isInstallmentInterstitialShown && <LazyInstallmentInterstitial />}
      {isSepaInterstitialOpen && <LazySepaInterstitial />}
      {isPhoneConfirmationOpen && <LazyPhoneConfirmationInterstitial />}
      {isOneTimePasswordConfirmationOpen && (
        <LazyOneTimePasswordConfirmationInterstitial />
      )}

      {isFormSubmitting && (
        <LoadingSpinner
          containToParent={true}
          overlayStyleOverrides={{
            backgroundColor: '#fff',
            opacity: 0.9,
          }}
        />
      )}
    </div>
  );
};

export { Form };
